export interface ApiErrorPayload {
  code: string
  message: string
  details?: unknown
  correlation_id?: string
}

export interface UserSession {
  access_token: string
  expires_in: number
  role: string
  user: { id: string; email: string; full_name: string }
  organization: {
    id: string; name: string; slug: string; currency: string; timezone: string
    language: string; trial_ends_at: string; bin?: string; kaspi_idaccount?: string
    schema_name: string; license_plan: 'start' | 'business' | 'pro'; license_status: string
    license_started_at: string; license_expires_at?: string
  }
}

interface ValidationDetail {
  loc?: unknown[]
  msg?: string
  type?: string
  ctx?: Record<string, unknown>
}

const FIELD_LABELS: Record<string, string> = {
  first_name: 'Имя',
  last_name: 'Фамилия',
  phone: 'Телефон',
  email: 'Email',
  portal_password: 'Пароль кабинета',
  branch_id: 'Филиал',
  payment_method: 'Способ оплаты',
}

export function apiErrorMessage(payload: ApiErrorPayload): string {
  if (payload.code !== 'VALIDATION_ERROR' || !Array.isArray(payload.details)) return payload.message
  const messages = (payload.details as ValidationDetail[]).map((detail) => {
    const field = [...(detail.loc ?? [])].reverse().find((item) => typeof item === 'string')
    const label = typeof field === 'string' ? (FIELD_LABELS[field] ?? field) : 'Данные формы'
    if (detail.type === 'string_too_short' && typeof detail.ctx?.min_length === 'number') {
      return `${label}: минимум ${detail.ctx.min_length} символов`
    }
    if (detail.type === 'string_too_long' && typeof detail.ctx?.max_length === 'number') {
      return `${label}: максимум ${detail.ctx.max_length} символов`
    }
    const message = detail.msg?.replace(/^Value error,\s*/i, '') ?? 'некорректное значение'
    return `${label}: ${message}`
  })
  return messages.length ? messages.join('. ') : payload.message
}

export class ApiError extends Error {
  constructor(public status: number, public payload: ApiErrorPayload) {
    super(apiErrorMessage(payload))
  }
}

const API_URL = import.meta.env.VITE_API_BASE_URL ?? ''

function csrfToken(): string {
  const item = document.cookie.split('; ').find((entry) => entry.startsWith('fitness_csrf='))
  return item ? decodeURIComponent(item.split('=').slice(1).join('=')) : ''
}

export function hasRefreshSession(): boolean {
  return Boolean(csrfToken())
}

export async function api<T>(
  path: string,
  options: RequestInit = {},
  accessToken?: string,
): Promise<T> {
  const headers = new Headers(options.headers)
  if (!headers.has('Content-Type') && options.body) headers.set('Content-Type', 'application/json')
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
  const response = await fetch(`${API_URL}${path}`, { ...options, headers, credentials: 'include' })
  if (!response.ok) {
    let payload: ApiErrorPayload
    try { payload = await response.json() as ApiErrorPayload }
    catch { payload = { code: `HTTP_${response.status}`, message: response.statusText } }
    throw new ApiError(response.status, payload)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export function refreshSession(): Promise<UserSession> {
  return api<UserSession>('/api/v1/auth/refresh', {
    method: 'POST', headers: { 'X-CSRF-Token': csrfToken() },
  })
}

export function idempotencyKey(prefix: string): string {
  return `${prefix}:${crypto.randomUUID()}`
}
